import React from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { getDocument } from '@atlaskit/browser-apis';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

interface DwellTimeConfig {
	contentARI: string;
}

/**
 * Hook to track dwell time on a page.
 * Measures how long users spend actively viewing content.
 * Only counts time when the page tab is visible/active.
 * Fires an event with total dwell time when the user leaves the page
 *
 * @example
 * useDwellTimeAnalytics({
 *   contentARI: 'ari:cloud:confluence:...',
 * });
 */
export function useDwellTimeAnalytics({ contentARI }: DwellTimeConfig): void {
	const dwellTimeRef = React.useRef(0); // total dwell time in ms
	const lastActiveTimeRef = React.useRef<number | null>(null);
	const isVisibleRef = React.useRef(true);
	const hasInteractedRef = React.useRef(false); // track if user has interacted with page
	const isInitializedRef = React.useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	React.useEffect(() => {
		if (!contentARI) {
			return;
		}

		const document = getDocument();
		if (!document) {
			return;
		}

		// Initialize on first effect run
		if (!isInitializedRef.current) {
			// eslint-disable-next-line @repo/internal/react/disallow-unstable-values
			lastActiveTimeRef.current = Date.now();
			dwellTimeRef.current = 0;
			isInitializedRef.current = true;
		}

		// Track user interactions (scroll, click, etc) to detect if page was actually viewed
		const handleInteraction = () => {
			hasInteractedRef.current = true;
		};

		// Handle tab visibility changes
		const handleVisibilityChange = () => {
			if (document.hidden) {
				// Tab is hidden - pause dwell time tracking
				isVisibleRef.current = false;
			} else {
				// Tab is visible again - resume tracking
				isVisibleRef.current = true;
				if (lastActiveTimeRef.current === null) {
					// eslint-disable-next-line @repo/internal/react/disallow-unstable-values
					lastActiveTimeRef.current = Date.now();
				}
			}
		};

		// fire [final] dwell time event
		const fireDwellTimeEvent = () => {
			if (dwellTimeRef.current === 0) {
				return;
			}

			const analyticsEvent = createAnalyticsEvent({
				action: 'dwellTime',
				actionSubject: 'page',
			});

			analyticsEvent.context = analyticsEvent.context || [];
			analyticsEvent.context.push({
				source: 'dwellTimeFinal',
			});

			fireUIAnalytics(analyticsEvent, 'dwellTimeFinal', {
				contentARI,
				dwellTime: dwellTimeRef.current,
			});
		};

		// Handle page unload (full page navigation or tab close)
		const handleBeforeUnload = () => {
			if (isVisibleRef.current && lastActiveTimeRef.current !== null) {
				// Update dwell time
				// eslint-disable-next-line @repo/internal/react/disallow-unstable-values
				const now = Date.now();
				const timeSinceLastUpdate = now - lastActiveTimeRef.current;
				dwellTimeRef.current += timeSinceLastUpdate;

				fireDwellTimeEvent();
			}
		};

		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		document.addEventListener('visibilitychange', handleVisibilityChange);

		// Set up interaction listeners to detect if page was actually viewed
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		document.addEventListener('scroll', handleInteraction, { passive: true });
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		document.addEventListener('click', handleInteraction, { passive: true });
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		document.addEventListener('keydown', handleInteraction, { passive: true });

		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		window.addEventListener('beforeunload', handleBeforeUnload);

		// cleanup - fire event when component unmounts e.g. client-side navigation
		return () => {
			// calculate total dwell time before unmounting
			if (isVisibleRef.current && lastActiveTimeRef.current !== null) {
				// eslint-disable-next-line @repo/internal/react/disallow-unstable-values
				const now = Date.now();
				const timeSinceLastUpdate = now - lastActiveTimeRef.current;
				dwellTimeRef.current += timeSinceLastUpdate;
			}

			// this filters out redirects but tracks all meaningful page views
			// this is a form of debouncing
			// as visiting a component page e.g. https://atlassian.design/components/calendar will redirect to https://atlassian.design/components/calendar/examples
			// without this, we would fire an event for the base page which is not relevant
			if (dwellTimeRef.current >= 500) {
				fireDwellTimeEvent();
			}

			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			document.removeEventListener('scroll', handleInteraction);
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			document.removeEventListener('click', handleInteraction);
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			document.removeEventListener('keydown', handleInteraction);
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [contentARI, createAnalyticsEvent]);
}
